import { useEffect, useCallback, useState } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@altimateai/lego";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@altimateai/lego";
import { IconInfoSquare } from "@tabler/icons-react";
import { WarehouseQueries } from "../../WarehouseQueries";
import { WarehouseDetails } from "../../types";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import WarehouseOpportunities from "./WarehouseOpportunities";
import WarehouseAutoTune from "./WarehouseAutoTune";
import { Graph } from "../../Graph";
import HeadingWithIcon from "@/components/icon/HeadingWithIcon";
import { DateRange } from "@/components/dateRange";
import { Stack } from "@altimateai/lego";
import { WH_DateRange_Prefix } from "../../constants";

interface WarehouseDetailsTabsProps {
  warehouseDetails: WarehouseDetails;
  startDate: Date;
  endDate: Date;
  isSchedulingEnabled: boolean;
  isDynamicSchedule: boolean;
}

const validTabs = ["0", "1", "2", "3"] as const;
type TabValue = (typeof validTabs)[number];

const WarehouseDetailsTabs = ({
  warehouseDetails,
  startDate,
  endDate,
  isSchedulingEnabled,
                                isDynamicSchedule
}: WarehouseDetailsTabsProps) => {
  const { instance_id } = useParams<{ instance_id: string }>();
  const { searchParams, updateSearchParamsByKey } = useFiltersBySearchParams();
  const [selectedView, setSelectedView] = useState("performance");

  // Get the tab index from URL params (default to 0)
  const activeTab = searchParams.get("tab") || "0";

  const handleTabChange = useCallback(
    (value: string) => {
      updateSearchParamsByKey({ tab: value });
    },
    [updateSearchParamsByKey]
  );

  const onUpdate = useCallback(
    (newStartDate: Date, newEndDate: Date) => {
      updateSearchParamsByKey({
        [`${WH_DateRange_Prefix}_start_date`]: newStartDate.toISOString(),
        [`${WH_DateRange_Prefix}_end_date`]: newEndDate.toISOString(),
      });
    },
    [updateSearchParamsByKey]
  );

  // Update URL if invalid tab index is provided
  useEffect(() => {
    if (!validTabs.includes(activeTab as TabValue)) {
      updateSearchParamsByKey({ tab: "0" });
    }
  }, [activeTab, updateSearchParamsByKey]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <Stack justify="between" align="center">
            <HeadingWithIcon icon={IconInfoSquare} title="Warehouse Details" />
            {activeTab !== "0" && selectedView !== "schedule" ? (
              <DateRange
                startDate={startDate}
                endDate={endDate}
                showTimezone={true}
                onDateRangeSelect={onUpdate}
                wrapperClass="al-text-base al-font-normal al-text-sm"
              />
            ) : null}
          </Stack>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs variant="underlined" value={activeTab} onValueChange={handleTabChange}>
          <TabsList>
            <TabsTrigger value="3">Overview</TabsTrigger>
            <TabsTrigger value="1">Auto Tune</TabsTrigger>
            <TabsTrigger value="0">Opportunities</TabsTrigger>
            <TabsTrigger value="2">Queries</TabsTrigger>
          </TabsList>
          <TabsContent value="0" className="al-mt-4">
            <WarehouseOpportunities warehouseDetails={warehouseDetails} />
          </TabsContent>
          <TabsContent value="3" className="al-mt-4">
            <Graph
              warehouse_rk={warehouseDetails.warehouse_rk}
              startDate={startDate}
              endDate={endDate}
              instance_id={instance_id!}
            />
          </TabsContent>
          <TabsContent value="1" className="al-mt-4">
            <WarehouseAutoTune
              warehouseDetails={warehouseDetails}
              startDate={startDate}
              endDate={endDate}
              isSchedulingEnabled={isSchedulingEnabled}
              isDynamicSchedule={isDynamicSchedule}
              selectedView={selectedView}
              setSelectedView={setSelectedView}
            />
          </TabsContent>
          <TabsContent value="2" className="al-mt-4">
            <WarehouseQueries
              startDate={startDate}
              endDate={endDate}
              warehouse={warehouseDetails.warehouse_name}
            />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default WarehouseDetailsTabs;
